import React, { ReactNode } from "react";
import { connect } from "react-redux";
import { PropertyDataType } from "src/backend/constants/datatypes";
import { formatWithLocale as formatDate } from "src/gantt/helpers/DateHelpers";
import { Dispatch, iRootState } from "src/rematch/store";
import { withTranslation } from "react-i18next";
import { translateObjectClassName, translateObjectTypeName, translateStateName, translateWorkflowName } from "src/backend/helpers/translationHelpers";
import IMfilesValueListItem from "src/backend/interfaces/mfiles/IMfilesValueListItem";
import {  IItemFormState,  ItemFormCommon } from "./ItemFormCommon";
import styled from "styled-components";
import { IItem, IItemProperty } from "src/backend/interfaces/IItem";
import { BuiltInPropertyDef } from "src/backend/constants/builtinProperties";
import { IObjectInsert } from "src/rematch/interfaces/IObjectInsert";
import { MFILES_DATE_VALUE_FORMAT } from "src/backend/constants/common";
import { mergeDateAndTime } from "src/backend/helpers/dateHelpers";
import { DataType } from "ajv/dist/compile/validate/dataType";

export const ObjectTypeLink = styled.li`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

class AddItemFormBase extends ItemFormCommon  {

  title(): string {
    const { t } = this.props;
    return t("editor.add");
  }

  initializeObjectType(objectTypeId: number): void {
    this.setState((prevState) => {
      return {
        ...prevState,
        ...this.getInitialState(objectTypeId)
      }
    });
  }

  getRelatedPropertyId(objectTypeId: number, relatedObjectTypeId: number): number {
    const objectTypeConfig = this.props.objectTypeConfigurations.get(relatedObjectTypeId);
    //Find relation from child config
    if(objectTypeConfig?.editConfig?.childObjectTypesAndProperties) {
      for(const [childObjectTypeId, childPropertyId] of objectTypeConfig.editConfig.childObjectTypesAndProperties) {
        if(childObjectTypeId === objectTypeId && childPropertyId) {
          return childPropertyId;
        }
      }
    }
    if(this.props.dragndropConfigurations) {
      //Find relation from drag and drop config
      for(const dndConfig of this.props.dragndropConfigurations) {
        if(dndConfig.sourceObjectType === relatedObjectTypeId && dndConfig.targetObjectType === objectTypeId && dndConfig.targetPropertyToUpdate) {
          return dndConfig.targetPropertyToUpdate;
        }
      }
      //Find relation from reversed drag and drop config
      for(const dndConfig of this.props.dragndropConfigurations) {
        if(dndConfig.targetObjectType === relatedObjectTypeId && dndConfig.sourceObjectType === objectTypeId && dndConfig.sourcePropertyToUpdate) {
          return dndConfig.sourcePropertyToUpdate;
        }
      }
    }
    return null;
  }

  getInitialItem(objectTypeId: number): IItem {
    const relatedItem = this.props.getItem(this.props.selectedItem);
    const relatedPropertyId = relatedItem ? this.getRelatedPropertyId(objectTypeId, relatedItem.objectType) : null;
    const newDate = new Date();
    const objectTypeConfig = this.props.objectTypeConfigurations.get(objectTypeId);
    const [defaultStartTime, defaultEndTime] = objectTypeConfig.timeDefaults ?? [null,null];
    const itemStart = relatedItem?.start ?? mergeDateAndTime(newDate, defaultStartTime);
    const itemEnd = relatedItem?.end ?? mergeDateAndTime(newDate, defaultEndTime);
    const itemProperties: IItemProperty[] = [];
    const itemClassId = objectTypeConfig.editConfig.defaultObjectClassId;
    const itemClassName = itemClassId ? translateObjectClassName(itemClassId, this.props.getClass(itemClassId)?.Name ?? "<DefaultClass>") : "";
    const itemWorkflowId = objectTypeConfig.editConfig.workflowId;
    const itemWorkflow = this.props.getWorkflow(itemWorkflowId);
    const itemWorkflowName = itemWorkflowId ? translateWorkflowName(itemWorkflowId, itemWorkflow?.Name ?? "<DefaultWorkflow>") : "";
    const itemStateId = objectTypeConfig.editConfig.defaultWorkflowStateId;
    const itemState = itemWorkflow?.States?.find(i => i.ID === itemStateId);
    const itemStateName = (itemStateId && itemWorkflowId) ? translateStateName(itemStateId, itemState?.Name ?? "<DefaultState>") : "";
    const editProperties = this.props.getEditProperties(objectTypeId);
    const defaultPropertyValues = objectTypeConfig.editConfig.defaultPropertyValues;

    const itemName = "";

    for ( const editProperty of editProperties.filter(i => i.updateOnly === false) ) {

      const isOwnProperty = typeof editProperty.propertyIdOrName === 'string';

      if(!isOwnProperty) {
        const propertyId = editProperty.propertyIdOrName as number;
        const propertyDef = this.props.getPropertyDefinition(propertyId);
        let propertyValue: string = null;
        let displayValue = "";

        if(propertyId === BuiltInPropertyDef.Class) {
          propertyValue = itemClassId?.toString();
          displayValue = itemClassName;
        }

        if(propertyId === BuiltInPropertyDef.Workflow) {
          propertyValue = itemWorkflowId?.toString();
          displayValue = itemWorkflowName;
        }

        if(propertyId === BuiltInPropertyDef.State) {
          propertyValue = itemStateId?.toString();
          displayValue = itemStateName;
        }

        const defaultPropertyValue = objectTypeConfig.editConfig.defaultPropertyValues.find(i => i.propertyId === propertyId);

        if (defaultPropertyValue) {

          propertyValue = defaultPropertyValue.defaultValue;
          displayValue = defaultPropertyValue.defaultValue;

          if (defaultPropertyValue.useDateTimeNow) {
            propertyValue = formatDate(newDate, MFILES_DATE_VALUE_FORMAT);
            if (propertyDef.DataType === PropertyDataType.Date) {
              displayValue = formatDate(newDate, this.props.dateFormat);
            } else if (propertyDef.DataType === PropertyDataType.Time) {
              displayValue = formatDate(newDate, this.props.timeFormat);
            }
          }
        }

        if(objectTypeConfig.dateProperties?.includes(propertyId)) {
          const startOrEnd = propertyId === objectTypeConfig.dateProperties[0] ? itemStart : itemEnd;
          propertyValue = formatDate(startOrEnd, MFILES_DATE_VALUE_FORMAT);
          displayValue = formatDate(startOrEnd, this.props.dateFormat);
        }

        if(objectTypeConfig.timeProperties?.includes(propertyId)) {
          const startOrEnd = propertyId === objectTypeConfig.timeProperties[0] ? itemStart : itemEnd;
          propertyValue = formatDate(startOrEnd, MFILES_DATE_VALUE_FORMAT);
          displayValue = formatDate(startOrEnd, this.props.timeFormat);
        }

        if(propertyId === relatedPropertyId) {
          propertyValue = relatedItem.objectId.toString();
          displayValue = relatedItem.name;
        }

        itemProperties.push({
          dataType: propertyDef.DataType,
          property: propertyId,
          value: propertyValue,
          displayValue: displayValue
        });
      }
    }

    if(itemClassId && !itemProperties.find(i => i.property === BuiltInPropertyDef.Class)) {
      const propertyDef = this.props.getPropertyDefinition(BuiltInPropertyDef.Class);
      itemProperties.push({
        dataType: propertyDef.DataType,
        property: propertyDef.ID,
        value: itemClassId.toString(),
        displayValue: itemClassName
      });
    }

    if(itemWorkflowId && !itemProperties.find(i => i.property === BuiltInPropertyDef.Workflow)) {
      const propertyDef = this.props.getPropertyDefinition(BuiltInPropertyDef.Workflow);
      itemProperties.push({
        dataType: propertyDef.DataType,
        property: propertyDef.ID,
        value: itemWorkflowId.toString(),
        displayValue: itemWorkflowName
      });
    }

    if(itemStateId && !itemProperties.find(i => i.property === BuiltInPropertyDef.State)) {
      const propertyDef = this.props.getPropertyDefinition(BuiltInPropertyDef.State);
      itemProperties.push({
        dataType: propertyDef.DataType,
        property: propertyDef.ID,
        value: itemStateId.toString(),
        displayValue: itemStateName
      });
    }

    if(relatedPropertyId && !itemProperties.find(i => i.property === relatedPropertyId)) {
      const propertyDef = this.props.getPropertyDefinition(relatedPropertyId);
      itemProperties.push({
        dataType: propertyDef.DataType,
        property: propertyDef.ID,
        value: relatedItem.objectId.toString(),
        displayValue: relatedItem.name
      });
    }

    // add default properties and set default values if property was not defined in editProperties config
    for ( const defProp of defaultPropertyValues ) {
      if ( !itemProperties.find(i => i.property === defProp.propertyId) ) {
        const propertyDef = this.props.getPropertyDefinition(defProp.propertyId);

        itemProperties.push({
          dataType: propertyDef.DataType,
          property: propertyDef.ID,
          value: defProp.defaultValue,
          displayValue: defProp.defaultValue
        });
      }
    }

    return {
      id: -1,
      objectId: -1,
      objectType: objectTypeId,
      objectVersion: 0,
      name: itemName,
      linksTo: [],
      linksFrom: [],
      isCheckedOut: false,
      isCheckedOutToThisUser: false,
      properties: itemProperties,
      color: null,
      status: itemStateId,
      start: itemStart,
      end: itemEnd,
    };
  }

  getInitialState(objectTypeId: number): IItemFormState {
    const item = this.getInitialItem(objectTypeId);
    const properties = this.getInitialProperties(item, true);
    return {
      item: item,
      properties: properties,
    };
  }

  saveForm = (): void => {
    const item = this.state.item;
    const insert: IObjectInsert = {
      objectType: item.objectType,
      properties: []
    };
    for(const property of this.state.properties) {
      if(property.hasError) {
        return;
      }
      if(property.value?.length > 0) {
        if(property.isOwnProperty) {
          const ownProperty = property.propertyIdOrName as string;
          const objectTypeConfig = this.props.objectTypeConfigurations.get(item.objectType);
          if(ownProperty === "start") {
            if(objectTypeConfig.dateProperties?.length === 2) {
              insert.properties.push({
                propertyId: objectTypeConfig.dateProperties[0],
                dataType: PropertyDataType.Date,
                value: property.value,
              });
            }
            if(objectTypeConfig.timeProperties?.length === 2) {
              insert.properties.push({
                propertyId: objectTypeConfig.timeProperties[0],
                dataType: PropertyDataType.Time,
                value: property.value,
              });
            }
          }
          if(ownProperty === "end") {
            if(objectTypeConfig.dateProperties?.length === 2) {
              insert.properties.push({
                propertyId: objectTypeConfig.dateProperties[1],
                dataType: PropertyDataType.Date,
                value: property.value,
              });
            }
            if(objectTypeConfig.timeProperties?.length === 2) {
              insert.properties.push({
                propertyId: objectTypeConfig.timeProperties[1],
                dataType: PropertyDataType.Time,
                value: property.value,
              });
            }
          }
        } else {
          insert.properties.push({
            propertyId: property.propertyIdOrName as number,
            dataType: property.dataType,
            value: property.value,
          });
        }
      }
    }

    for(const property of item.properties) {
      if(property.value !== null) {
        const insertProperty = insert.properties.find(i => i.propertyId === property.property);
        if(!insertProperty) {
          insert.properties.push({
            propertyId: property.property,
            dataType: property.dataType,
            value: property.value,
          });
        }
      }
    }

    if(insert.properties.length > 0) {
      this.props.insertItemAsync(insert);
    }
    this.props.toggleShowForm();
  }
  
  renderProperties(): ReactNode {
    if(this.state.item === null) {
      const { t } = this.props;
      const links = [];
      let i = 0;
      for(const objectTypeConfig of this.props.objectTypeConfigurations.values()) {
        if(objectTypeConfig.mayEdit && objectTypeConfig.editConfig) {
          const linkName = t("editor.newobject", { type: translateObjectTypeName(objectTypeConfig.objectTypeId, objectTypeConfig.name) });
          links.push(<ObjectTypeLink key={i++} onClick={(_) => this.initializeObjectType(objectTypeConfig.objectTypeId)}>{linkName}</ObjectTypeLink>);
        }
      }
      return links;
    } else {
      return super.renderProperties();
    }
  }
  
}

export const AddItemForm = withTranslation()(connect(
  (state: iRootState) => ({
    httpClient: state.common.httpClient,
    selectedItem: state.items.selectedItem,
    getItem: (itemId: number) => state.items.allData[itemId],
    getEditProperties: (objectType: number) => state.common.configuration.objectTypeConfigurations.get(objectType)?.editConfig?.editProperties ?? [],
    getPropertyDefinition: (propertyId: number) => state.common.configuration.propertyDefinitions.get(propertyId),
    getValueListDefinition: (valueListId: number) => state.common.configuration.valueListDefinitions.get(valueListId),
    getValueListItems: (valueListId: number) => state.common.configuration.valueListItems[valueListId],
    getWorkflow: (workflowId: number) =>  state.common.configuration.workflows.get(workflowId),
    getClass: (classId: number) => state.common.configuration.classes.get(classId),
    objectTypeConfigurations: state.common.configuration.objectTypeConfigurations,
    dragndropConfigurations: state.common.configuration.dragndropConfigurations,
    valueListItems: state.common.configuration.valueListItems,
    dateFormat: state.UI.displaySettings.dateFormat,
    timeFormat: state.UI.displaySettings.timeFormat,
    showTimeInTable: state.UI.displaySettings.showTimeInTable,
  }), 
  (dispatch: Dispatch) => ({
    toggleShowForm: dispatch.UI.toggleShowAddWorkBox,
    updateManyAsync: dispatch.items.updateManyAsync,
    insertItemAsync: dispatch.items.insertItemAsync,
    setValueListItems: (valueListId: number, valueListItems: IMfilesValueListItem[]) => dispatch.common.setValueListItems(valueListId, valueListItems),
  })
)(AddItemFormBase));
